<template>
  <div>
    <h1 class="heading-1 mb-24">{{ i18n(translations.title) }}</h1>
    <p class="body-1 mb-24">{{ i18n(translations.description) }}</p>

    <RevLoadingScreen
      v-if="isSaving || isLoading"
      :text="
        i18n(
          isSaving
            ? translations.savingLegalRepresentativeDetails
            : translations.loadingLegalRepresentativeDetails,
        )
      "
    />
    <RevForm
      v-else
      :id="FORM_ID"
      :initial-values="initialFormValues"
      :name="FORM_ID"
      :validate="validate()"
      @submit="handleSubmit"
    >
      <template #default="{ values, hasError, errors }">
        <div class="mb-36">
          <RevCheckbox
            id="same_as_account_requester"
            :model-value="isSameAsAccountRequester"
            @update:model-value="
              (value) => handleCheckSameAsAccountRequester(!!value)
            "
          >
            <template #label>
              {{ i18n(translations.sameAsAccountRequesterLabel) }}
            </template>
          </RevCheckbox>
        </div>

        <StakeholderForm
          v-model:address-line1="values.addressLine1"
          v-model:address-line2="values.addressLine2"
          v-model:city="values.city"
          v-model:country="values.country"
          v-model:country-of-birth="values.countryOfBirth"
          v-model:country-of-citizenship="values.countryOfCitizenship"
          v-model:country-of-issue="values.countryOfIssue"
          v-model:country-of-residency="values.countryOfResidency"
          v-model:date-of-birth="values.dateOfBirth"
          v-model:first-name="values.firstName"
          v-model:identification-type="values.identificationType"
          v-model:kana-first-name="values.kanaFirstName"
          v-model:kana-last-name="values.kanaLastName"
          v-model:last-name="values.lastName"
          v-model:latin-first-name="values.latinFirstName"
          v-model:latin-last-name="values.latinLastName"
          v-model:phone-number="values.phoneNumber"
          v-model:photo-id-back="values.photoIdBack"
          v-model:photo-id-front="values.photoIdFront"
          v-model:postal-code="values.postalCode"
          :disabled="isFormDisabled"
          :errors
          :feature-code="FeatureCode.WEB_KYB_LEGAL_REPRESENTATIVE"
        />

        <div
          class="mx-auto mt-48 flex w-[328px] items-center justify-center gap-16"
        >
          <RevButton
            class="w-full"
            :disabled="hasError || isSaving"
            :form="FORM_ID"
            :loading="isSaving"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.saveButton) }}
          </RevButton>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useHead } from '#imports'
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevForm, makeValidate } from '@ds/components/Form'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { StakeholderRole } from '~/api/kyb/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useKybData } from '~/scopes/kyb/composables/useKybData'
import { useKybStakeholder } from '~/scopes/kyb/composables/useKybStakeholder'
import type { StakeholderFormValues } from '~/scopes/kyb/pages/components/steps/components/StakeholderForm/StakeholderForm.types'
import { createStakeholderFormValues } from '~/scopes/kyb/utils/kyb.utils'

import StakeholderForm from '../components/StakeholderForm/StakeholderForm.vue'

import translations from './LegalRepresentative.translations'

const FORM_ID = 'kyb_legal_representative_form'

const i18n = useI18n()
useHead({ title: i18n(translations.pageTitle) })
const { openSuccessToast, openErrorToast } = useTheToast()
const {
  legalRepresentative,
  accountRequester,
  refreshKybData,
  saveStakeholder,
  addRolesToStakeholder,
  isLoading,
} = useKybData()
const { stakeholderValidationRules, fillFormAsSameAs } = useKybStakeholder()

const isSaving = ref(false)

const isFormDisabled = ref(false)
const isSameAsAccountRequester = ref(false)

const initialFormValues = computed<StakeholderFormValues>(() =>
  createStakeholderFormValues(legalRepresentative.value),
)

function handleCheckSameAsAccountRequester(value: boolean) {
  if (value === true) {
    isFormDisabled.value = true
    isSameAsAccountRequester.value = true
    fillFormAsSameAs(FORM_ID, accountRequester.value)
  } else {
    isFormDisabled.value = false
    isSameAsAccountRequester.value = false
    fillFormAsSameAs(FORM_ID, legalRepresentative.value)
  }
}

function validate() {
  return makeValidate<StakeholderFormValues>({
    ...stakeholderValidationRules.value,
  })
}

async function handleSubmit(formValues: StakeholderFormValues) {
  isSaving.value = true

  try {
    if (isSameAsAccountRequester.value && accountRequester.value) {
      await addRolesToStakeholder(accountRequester.value, [
        StakeholderRole.LEGAL_REPRESENTATIVE,
      ])
    } else {
      await saveStakeholder(formValues, [StakeholderRole.LEGAL_REPRESENTATIVE])
    }

    openSuccessToast({
      title: i18n(translations.saveSuccessTitle),
      content: i18n(translations.saveSuccessContent),
    })

    navigateTo({ name: ROUTE_NAMES.KYB.STEPS.STEP_4 })
    refreshKybData()
  } catch (error) {
    const { title: content } = HttpApiError.fromAnyError(error as Error)

    openErrorToast({
      title: i18n(translations.saveErrorTitle),
      content,
    })
  } finally {
    isSaving.value = false
  }
}
</script>
